/**
 * CopyIntakeDialog – copy food intake records between days or clients.
 *
 * Supports two entry points:
 *  1. "Copy Day" button at the top of the Intake page (copies the whole day
 *     or a subset of categories selected by the user inside this dialog).
 *  2. Pre-selected category checkboxes passed via `preselectedKategoriaIds`
 *     when the user ticks checkboxes on individual MealCategoryCards.
 *
 * Admin-only.
 */

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Copy } from "lucide-react";
import { getMealCategoryDisplayName } from "../utils";
import type { PrijemKategoria, Klient } from "../types";

interface CopyIntakeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Source client */
  sourceKlientId: number;
  /** Source date (YYYY-MM-DD) */
  sourceDatum: string;
  /** All available meal categories */
  prijemKategorie: PrijemKategoria[];
  /** All clients (for cross-client copy) */
  klienti: Klient[];
  /**
   * Pre-selected category IDs (from per-card checkboxes).
   * When provided the dialog opens in "selected categories" mode.
   */
  preselectedKategoriaIds?: number[];
  /** Called after a successful copy */
  onCopied: () => void;
}

type CopyMode = "append" | "replace";

export function CopyIntakeDialog({
  open,
  onOpenChange,
  sourceKlientId,
  sourceDatum,
  prijemKategorie,
  klienti,
  preselectedKategoriaIds,
  onCopied,
}: CopyIntakeDialogProps) {
  const { t } = useTranslation();

  // Target state
  const [targetKlientId, setTargetKlientId] = useState<number>(sourceKlientId);
  const [targetDatum, setTargetDatum] = useState<string>(sourceDatum);

  // Category selection
  const [selectedKategoriaIds, setSelectedKategoriaIds] = useState<Set<number>>(
    () => new Set(preselectedKategoriaIds ?? [])
  );
  const allSelected = selectedKategoriaIds.size === 0;

  // Copy mode
  const [mode, setMode] = useState<CopyMode>("append");
  const [copying, setCopying] = useState(false);

  const copyMutation = trpc.prijem.copy.useMutation();
  const utils = trpc.useUtils();

  const toggleKategoria = (id: number) => {
    setSelectedKategoriaIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleCopy = async () => {
    if (!targetKlientId || !targetDatum) return;
    setCopying(true);
    try {
      const kategoriaIds =
        selectedKategoriaIds.size > 0
          ? Array.from(selectedKategoriaIds)
          : undefined;

      await copyMutation.mutateAsync({
        sourceKlientId,
        sourceDatum,
        targetKlientId,
        targetDatum,
        kategoriaIds,
        replace: mode === "replace",
      });

      toast.success(
        t("mealLibrary:copy.success", "Intake copied successfully")
      );
      utils.prijem.getByKlientAndDate.invalidate();
      utils.prijem.getDailyTotals.invalidate();
      onCopied();
      onOpenChange(false);
    } catch {
      toast.error(t("mealLibrary:copy.error", "Failed to copy intake"));
    } finally {
      setCopying(false);
    }
  };

  // Reset state when dialog opens
  const handleOpenChange = (v: boolean) => {
    if (v) {
      setTargetKlientId(sourceKlientId);
      setTargetDatum(sourceDatum);
      setSelectedKategoriaIds(new Set(preselectedKategoriaIds ?? []));
      setMode("append");
    }
    onOpenChange(v);
  };

  const sourceClient = klienti.find(k => k.id === sourceKlientId);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            {t("mealLibrary:copy.title", "Copy Intake")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Source summary */}
          <div className="rounded-md bg-muted/40 px-3 py-2 text-sm">
            <span className="text-muted-foreground">
              {t("mealLibrary:copy.from", "From")}:{" "}
            </span>
            <strong>{sourceClient?.alias ?? sourceKlientId}</strong>
            <span className="mx-1 text-muted-foreground">·</span>
            <strong>{sourceDatum}</strong>
          </div>

          {/* Target client */}
          <div className="space-y-1.5">
            <Label>{t("mealLibrary:copy.targetClient", "Target client")}</Label>
            <Select
              value={String(targetKlientId)}
              onValueChange={v => setTargetKlientId(Number(v))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {klienti.map(k => (
                  <SelectItem key={k.id} value={String(k.id)}>
                    {k.alias}
                    {k.id === sourceKlientId && (
                      <span className="ml-1 text-muted-foreground text-xs">
                        ({t("mealLibrary:copy.sameClient", "same")})
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Target date */}
          <div className="space-y-1.5">
            <Label>{t("mealLibrary:copy.targetDate", "Target date")}</Label>
            <Input
              type="date"
              value={targetDatum}
              onChange={e => setTargetDatum(e.target.value)}
            />
          </div>

          {/* Category selection */}
          <div className="space-y-2">
            <Label>
              {t("mealLibrary:copy.categories", "Meal categories to copy")}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t(
                "mealLibrary:copy.categoriesHint",
                "Leave all unchecked to copy the entire day."
              )}
            </p>
            <div className="space-y-1.5">
              {prijemKategorie.map(kat => {
                const checked = selectedKategoriaIds.has(kat.id);
                return (
                  <div key={kat.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`kat-${kat.id}`}
                      checked={checked}
                      onCheckedChange={() => toggleKategoria(kat.id)}
                    />
                    <label
                      htmlFor={`kat-${kat.id}`}
                      className="text-sm cursor-pointer"
                    >
                      {getMealCategoryDisplayName(kat.nazov ?? kat.name ?? "")}
                    </label>
                  </div>
                );
              })}
            </div>
            {allSelected && (
              <p className="text-xs text-primary font-medium">
                {t(
                  "mealLibrary:copy.allCategories",
                  "All categories will be copied."
                )}
              </p>
            )}
          </div>

          {/* Append / Replace */}
          <div className="space-y-2">
            <Label>
              {t("mealLibrary:copy.mode", "If target already has data")}
            </Label>
            <RadioGroup
              value={mode}
              onValueChange={v => setMode(v as CopyMode)}
              className="space-y-1"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="append" id="mode-append" />
                <label htmlFor="mode-append" className="text-sm cursor-pointer">
                  {t("mealLibrary:copy.append", "Append (keep existing items)")}
                </label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="replace" id="mode-replace" />
                <label
                  htmlFor="mode-replace"
                  className="text-sm cursor-pointer text-destructive"
                >
                  {t(
                    "mealLibrary:copy.replace",
                    "Replace (delete existing items first)"
                  )}
                </label>
              </div>
            </RadioGroup>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button onClick={handleCopy} disabled={copying || !targetDatum}>
            {copying
              ? t("mealLibrary:copy.copying", "Copying…")
              : t("mealLibrary:copy.confirm", "Copy")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
